import { resources } from '@src/resources';
import { type CoverageRow, Outcome, outcomeLabel } from '@src/services/opsService';

type CoverageCountField = 'updatedCount' | 'noChangeCount' | 'noDataFoundCount';

/**
 * Maps each `Outcome` enum member to the `CoverageRow` field the backend's `enrichment_coverage`
 * view actually computes a count for. `EXCLUDED_FROM_SAMPLE`/`ERROR` have no entry: they're
 * run-level outcomes (tracked per `enrichment_runs` row, see axetay/really-enrich#67), but these
 * three fields come from the `enrichments` table -- the latest-record store, one row per
 * property/scope, written ONLY on a successful enrichment attempt (`PostgresEnrichmentStore.upsert`,
 * called from exactly one place: the success path of `packages/app/src/lambda/handler.ts`). A
 * skipped or failed run never writes an `enrichments` row, so its `result` column can never hold
 * those two values and the coverage view has no per-outcome count to report for them -- they fall
 * into the `NEVER_RUN` bucket below instead, alongside anything still pending.
 *
 * Built by filtering `Object.values(Outcome)` (not a fixed list) so a future outcome that DOES
 * become record-store-backed only needs an entry added here -- not a parallel bucket list kept in
 * sync by hand, the same way the Runs screen's outcome filter tracks the enum automatically.
 */
const COVERAGE_COUNT_FIELD: Partial<Record<Outcome, CoverageCountField>> = {
  [Outcome.UPDATED]: 'updatedCount',
  [Outcome.NO_CHANGE]: 'noChangeCount',
  [Outcome.NO_DATA_FOUND]: 'noDataFoundCount',
};

export const COVERAGE_OUTCOMES: readonly Outcome[] = Object.values(Outcome).filter(
  (outcome): outcome is Outcome => outcome in COVERAGE_COUNT_FIELD,
);

/** Sentinel key for the "ingested but not yet successfully enriched" bucket -- sampled out,
 * failing every attempt, or still pending. Not a real `Outcome` (it's the absence of one), so it
 * can't be a member of that enum; kept in the same segment-key space so CoverageSegment/
 * OUTCOME_COLOR/legends can treat all four buckets uniformly. */
export const NEVER_RUN = 'NEVER_RUN' as const;
export type CoverageSegmentKey = Outcome | typeof NEVER_RUN;

/** All four segment keys a Coverage bar/legend renders, in display order. */
export const COVERAGE_SEGMENT_KEYS: readonly CoverageSegmentKey[] = [...COVERAGE_OUTCOMES, NEVER_RUN];

export function coverageSegmentLabel(key: CoverageSegmentKey): string {
  return key === NEVER_RUN ? resources.coverage.neverRunLabel : outcomeLabel(key);
}

/** Chart-token colors, matched to the validated prototype's Updated/No change/No data found
 * palette. Shared by the Coverage screen and the Overview tiles' mini coverage bars, so both
 * stay in sync. `FALLBACK_OUTCOME_COLOR` covers a future COVERAGE_SEGMENT_KEYS member added here
 * without a matching color yet -- an unstyled bar segment instead of a broken one. */
export const OUTCOME_COLOR: Partial<Record<CoverageSegmentKey, string>> = {
  [Outcome.UPDATED]: 'bg-chart-1',
  [Outcome.NO_CHANGE]: 'bg-chart-2',
  [Outcome.NO_DATA_FOUND]: 'bg-chart-3',
  [NEVER_RUN]: 'bg-muted-foreground/30',
};
export const FALLBACK_OUTCOME_COLOR = 'bg-chart-4';

export interface CoverageSegment {
  outcome: CoverageSegmentKey;
  label: string;
  count: number;
  /** Share of `totalIngested` (every property this scope has ever been triggered for), 0-100.
   * Unrounded -- round only when rendering a label. */
  pct: number;
}

/**
 * Splits a CoverageRow into its known-outcome segments (from COVERAGE_OUTCOMES) plus a real
 * `NEVER_RUN` segment, each sized as a percentage of `totalIngested` -- every distinct property
 * this scope has ever been triggered for (success or not), not just the ones with a successful
 * record. See `enrichment_coverage`'s migration for how `totalIngested`/`neverRunCount` are
 * computed with no dependency on crawl's own catalog data. A scope with nothing ingested yet
 * (totalIngested === 0) gets 0% segments rather than dividing by zero.
 */
export function computeCoverageSegments(row: CoverageRow): CoverageSegment[] {
  const total = row.totalIngested;
  const pctOf = (count: number) => (total > 0 ? (count / total) * 100 : 0);

  const outcomeSegments = COVERAGE_OUTCOMES.map((outcome) => {
    const count = row[COVERAGE_COUNT_FIELD[outcome]!];
    return { outcome, label: coverageSegmentLabel(outcome), count, pct: pctOf(count) };
  });

  return [
    ...outcomeSegments,
    {
      outcome: NEVER_RUN,
      label: coverageSegmentLabel(NEVER_RUN),
      count: row.neverRunCount,
      pct: pctOf(row.neverRunCount),
    },
  ];
}
